import { ReactNode, useState } from 'react'
import { AccessTime } from '@mui/icons-material'
import {
  Alert,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Trans, useTranslation } from 'react-i18next'
import Form from '../../Form'
import TwoFactorCodeField, {
  TwoFactorCodeFormFields,
  useTwoFactorCodeForm,
} from '../../TwoFactorCodeField'
import {
  SectionTitle,
  StepActions,
  StepContent,
  TextActionLink,
} from '../ReauthenticationDialog.style'
import SupportInfoStep from './SupportInfoStep'

interface CodeStepProps {
  email: string
  notices: ReactNode
  error: string | null
  isBusy: boolean
  onSubmit: (code: string) => Promise<void>
  onBack: () => void
}

// Going back unmounts the step, which clears the code
const CodeStep = ({
  email,
  notices,
  error,
  isBusy,
  onSubmit,
  onBack,
}: CodeStepProps) => {
  const { t } = useTranslation()
  const { formProps, formError } = useTwoFactorCodeForm(onSubmit, error)
  const [isSupportInfoVisible, setIsSupportInfoVisible] = useState(false)

  if (isSupportInfoVisible)
    return <SupportInfoStep onBack={() => setIsSupportInfoVisible(false)} />

  return (
    <Form {...formProps}>
      <StepContent>
        <Stack spacing={2.5}>
          <DialogContentText variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="reauthentication.codeDescription"
              values={{ email }}
              components={[<strong key={0} />]}
            />
          </DialogContentText>
          {notices}
          {formError && <Alert severity="error">{formError}</Alert>}
          <div>
            <SectionTitle variant="h3">
              {t('reauthentication.code')}
            </SectionTitle>
            <TwoFactorCodeField
              required
              name={TwoFactorCodeFormFields.Code}
              label={t('reauthentication.code')}
              disabled={isBusy}
            />
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ mt: 1.75, color: 'grey.600' }}
            >
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontSize={13}>
                {t('reauthentication.codeRefreshHint')}
              </Typography>
            </Stack>
          </div>
          <TextActionLink
            component="button"
            type="button"
            variant="body2"
            underline="none"
            onClick={() => setIsSupportInfoVisible(true)}
          >
            {t('reauthentication.noAccessToApp')}
          </TextActionLink>
        </Stack>
      </StepContent>
      <StepActions disableSpacing>
        <Button variant="outlined" onClick={onBack} disabled={isBusy}>
          {t('reauthentication.goBack')}
        </Button>
        <LoadingButton type="submit" variant="contained" loading={isBusy}>
          {t('reauthentication.verify')}
        </LoadingButton>
      </StepActions>
    </Form>
  )
}

export default CodeStep
